import { ForwardedRef, forwardRef, ReactNode } from 'react'
import styled, { RuleSet } from 'styled-components'
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'
import { Autoplay, EffectCreative, Pagination } from 'swiper/modules'
import type { Swiper as SwiperClass } from 'swiper/types'
import { SwiperRef } from 'swiper/swiper-react'

import 'swiper/css'
import 'swiper/css/effect-creative'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

// z-index전환시 필요한 슬라이드 폭 기준
const ENTER_THRESHOLD = 0.01

export interface SwiperPropsType {
  children: ReactNode
  $wrapstyle?: RuleSet<object>
  onActiveIndexChangeFn?: (activeIdx: number) => void
  type: 'creative' | 'default'
  gap?: number
  swiperOption?: SwiperProps
}

const CustomSwiperWrap = forwardRef<SwiperRef, SwiperPropsType>(
  (
    { gap, onActiveIndexChangeFn, swiperOption, type, children, $wrapstyle },
    swiperRef: ForwardedRef<SwiperRef>,
  ) => {
    const swiperOptions: { [key: string]: SwiperProps } = {
      default: {
        grabCursor: true,
        slidesPerView: 'auto',
        onActiveIndexChange: (swiper: SwiperClass) => onActiveIndexChangeFn?.(swiper.activeIndex),
        spaceBetween: gap as number,
        modules: [Pagination, Autoplay],
        pagination: { type: 'fraction' },
        ...swiperOption,
      },
      creative: {
        grabCursor: true,
        effect: 'creative',
        slidesPerView: 'auto',
        centeredSlides: true,
        watchSlidesProgress: true, // slide.progress 실시간 계산
        pagination: { type: 'fraction' },
        creativeEffect: {
          prev: { shadow: true, translate: ['-2%', '-0.5%', 0] },
          next: { shadow: true, translate: ['103%', 0, 0] },
        },
        modules: [EffectCreative, Pagination],
        onActiveIndexChange: (swiper: SwiperClass) => onActiveIndexChangeFn?.(swiper.activeIndex),
        onProgress: (swiper: SwiperClass) => {
          swiper.slides.forEach((slideEl: HTMLElement & { progress?: number }) => {
            const progress = slideEl.progress ?? 1
            const absProgress = Math.abs(progress)

            // 0을 향해 ENTER_THRESHOLD 이상 이동한 슬라이드 = 진입 중인 카드
            const isEntering = absProgress > 0 && absProgress < 1 - ENTER_THRESHOLD

            slideEl.classList.toggle('is-entering-top', isEntering)
          })
        },
        ...swiperOption,
      },
    }
    return (
      <SwiperContainer {...swiperOptions[type]} $wrapstyle={$wrapstyle} ref={swiperRef}>
        {children}
      </SwiperContainer>
    )
  },
)

const SwiperContainer = styled(Swiper)<{ $wrapstyle?: RuleSet<object> }>`
  ${(props) => props.$wrapstyle};

  .swiper-slide.is-entering-top {
    z-index: 20 !important;
  }
`
const SwiperItemsWrap = styled(SwiperSlide)<{ $itemstyle?: RuleSet<object> }>`
  width: fit-content;
  ${(props) => props.$itemstyle};
`

export const CustomSwiper = {
  Wrap: CustomSwiperWrap,
  Item: SwiperItemsWrap,
}